'use client';

import { useEffect, useState, memo, useCallback, useMemo } from 'react';
import {
  TableRow,
  TableCell,
  Box,
  Typography,
  IconButton,
  TextField,
  Select,
  MenuItem,
  FormControl,
  Chip,
  Tooltip,
  Switch,
  FormControlLabel,
  alpha,
  Button,
  Stack,
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  Delete as DeleteIcon,
  LocationOn as LocationIcon,
  Edit as EditIcon,
  Check as CheckIcon,
} from '@mui/icons-material';

import DeliveryAddressAccordion from './DeliveryAddressAccordion';
import BulkBadge from '@/components/BulkBadge';
import DimensionsInput from '@/components/PriceEstimator/components/DimensionsInput';
import ParcelTypeSelector from '@/components/PriceEstimator/components/ParcelTypeSelector';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { useTranslation } from '@/kit/useTranslation';

const BUSINESS_COLORS = {
  primary: '#1a1a1a',
  accent: '#f59e0b',
  success: '#10b981',
};

const CLUSTER_COLORS = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'];

function haversineKm(lat1, lng1, lat2, lng2) {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

const CATEGORIES = [
  { value: 'GENERAL', labelKey: 'bulkBooking.categoryGeneral' },
  { value: 'DOCUMENTS', labelKey: 'bulkBooking.categoryDocuments' },
  { value: 'ELECTRONICS', labelKey: 'bulkBooking.categoryElectronics' },
  { value: 'FRAGILE', labelKey: 'bulkBooking.categoryFragile' },
  { value: 'FOOD', labelKey: 'bulkBooking.categoryFoodPerishable' },
  { value: 'CLOTHING', labelKey: 'bulkBooking.categoryClothing' },
  { value: 'MEDICAL', labelKey: 'bulkBooking.categoryMedical' },
];

function BulkBookingRow({
  item,
  index,
  primaryPickup,
  onUpdate,
  onDelete,
  onDuplicate,
  bulkOrder,
  showBulkBadge = true,
}) {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const [localWeight, setLocalWeight] = useState(item.weightKg || 1);
  const [localVolume, setLocalVolume] = useState(item.volumeCbm || 0);

  // Dimensions object (cm)
  const initialVolume = item.volume || { length: '', breadth: '', height: '' };
  const [volumeDims, setVolumeDims] = useState(initialVolume);
  const [showVolumeEditor, setShowVolumeEditor] = useState(false);
  const [showCategoryEditor, setShowCategoryEditor] = useState(false);

  // Category / parcel types (allow multiple selection)
  const [selectedTypes, setSelectedTypes] = useState(
    item.parcelType && Array.isArray(item.parcelType) && item.parcelType.length > 0
      ? item.parcelType
      : item.category
        ? [item.category]
        : []
  );

  // Keep selectedTypes in sync if item.parcelType changes from server updates
  useEffect(() => {
    const fromItem =
      item.parcelType && Array.isArray(item.parcelType) && item.parcelType.length > 0
        ? item.parcelType
        : item.category
          ? [item.category]
          : [];
    setSelectedTypes(fromItem);
  }, [item.parcelType, item.category]);

  // Helper to generate a compact, user-friendly label for parcel types
  const getParcelTypeLabel = (parcelTypeArr, category) => {
    const arr =
      parcelTypeArr && Array.isArray(parcelTypeArr) && parcelTypeArr.length
        ? [...parcelTypeArr]
        : category
          ? [category]
          : [];
    if (!arr || arr.length === 0) return t('bulkBooking.other');

    // If there are any non-GENERAL types, ignore GENERAL entirely for the displayed label
    const nonGeneral = arr.filter((t) => t && t.toUpperCase() !== 'GENERAL');
    const source = nonGeneral.length ? nonGeneral : arr;

    // Deduplicate while preserving order
    const uniq = [...new Set(source.map((t) => (t ? t : '').toString()))].filter(Boolean);

    if (uniq.length <= 2) return uniq.join(', ');
    return `${uniq.slice(0, 2).join(', ')} +${uniq.length - 2}`;
  };

  // Close other accordions when opening one
  const handleToggleAccordion = useCallback((type) => {
    if (type === 'address') {
      setIsExpanded((prev) => !prev);
      setShowVolumeEditor(false);
      setShowCategoryEditor(false);
    } else if (type === 'volume') {
      setShowVolumeEditor((prev) => !prev);
      setIsExpanded(false);
      setShowCategoryEditor(false);
    } else if (type === 'category') {
      setShowCategoryEditor((prev) => !prev);
      setIsExpanded(false);
      setShowVolumeEditor(false);
    }
  }, []);

  const handleTypeToggle = useCallback((type) => {
    setSelectedTypes((prev) => {
      const exists = prev.includes(type);
      if (exists) return prev.filter((t) => t !== type);
      return [...prev, type];
    });
  }, []);

  const getPrimaryType = (types) => {
    if (!types || types.length === 0) return 'GENERAL';
    const nonGeneral = types.find((t) => t && t.toUpperCase() !== 'GENERAL');
    return nonGeneral || types[0] || 'GENERAL';
  };

  const applyTypes = useCallback(() => {
    const primary = getPrimaryType(selectedTypes);
    onUpdate(item.itemId, { parcelType: selectedTypes, category: primary });
    setShowCategoryEditor(false);
  }, [selectedTypes, item.itemId, onUpdate]);

  // Safe display value for volume (always format numbers)
  const numLocalVolume = Number(localVolume);
  const displayVolume = Number.isFinite(numLocalVolume) ? numLocalVolume.toFixed(4) : null;

  // Format address for display
  const formatAddress = (address) => {
    if (!address) return t('bulkBooking.notSet');
    let addr;
    if (typeof address === 'string') {
      addr = address;
    } else {
      addr =
        address.fullAddress ||
        address.address ||
        address.formatted_address ||
        t('bulkBooking.notSet');
    }
    // Truncate to 50 chars to prevent UI stretching
    return addr.length > 30 ? addr.substring(0, 30) + '...' : addr;
  };

  // Handle weight change with debounce
  const handleWeightChange = useCallback((e) => {
    const value = parseFloat(e.target.value) || 0;
    setLocalWeight(value);
  }, []);

  const handleWeightBlur = useCallback(() => {
    if (localWeight !== item.weightKg) {
      onUpdate(item.itemId, { weightKg: localWeight });
    }
  }, [localWeight, item.itemId, item.weightKg, onUpdate]);

  const handleVolumeDimsChange = useCallback((vol) => {
    setVolumeDims(vol);
  }, []);

  const handleVolumeDimsBlur = useCallback(() => {
    const { length = 0, breadth = 0, height = 0 } = volumeDims || {};
    const l = Number(length) || 0;
    const b = Number(breadth) || 0;
    const h = Number(height) || 0;
    const cbm = l && b && h ? (l * b * h) / 1000000 : 0;
    setLocalVolume(cbm);
    onUpdate(item.itemId, {
      volumeCbm: cbm,
      volume: { length: l, breadth: b, height: h },
    });
  }, [volumeDims, item.itemId, onUpdate]);

  // Handle volume change
  const handleVolumeChange = useCallback((e) => {
    const value = parseFloat(e.target.value) || 0;
    setLocalVolume(value);
  }, []);

  const handleVolumeBlur = useCallback(() => {
    if (localVolume !== item.volumeCbm) {
      onUpdate(item.itemId, { volumeCbm: localVolume });
    }
  }, [localVolume, item.itemId, item.volumeCbm, onUpdate]);

  // Handle category change
  const handleCategoryChange = useCallback(
    (e) => {
      onUpdate(item.itemId, { category: e.target.value });
    },
    [item.itemId, onUpdate]
  );

  // Handle use primary pickup toggle
  const handleUsePrimaryToggle = useCallback(
    (e) => {
      onUpdate(item.itemId, { usePrimaryPickup: e.target.checked });
    },
    [item.itemId, onUpdate]
  );

  // Handle delivery address update
  const handleDeliveryUpdate = useCallback(
    (addressData) => {
      // Pass full address object to backend (matching individual parcel format)
      onUpdate(item.itemId, {
        deliveryAddress: addressData.addressObject, // Full object with type, latitude, longitude, formatted_address
        recipientName: addressData.recipientName,
        recipientPhone: addressData.recipientPhone,
        saveDeliveryAddress: addressData.saveDeliveryAddress,
      });
      setIsExpanded(false);
    },
    [item.itemId, onUpdate]
  );

  // Check if delivery address exists (address can be object or have lat/lng)
  const hasDelivery =
    item.deliveryAddress &&
    ((typeof item.deliveryAddress === 'object' && item.deliveryAddress.latitude) ||
      item.deliveryLat);

  // Compute display distance: use backend value if available, otherwise compute from coords
  const displayDistanceKm = useMemo(() => {
    if (item.distanceKm) return Number(item.distanceKm).toFixed(1);
    const pickupLat = primaryPickup?.lat;
    const pickupLng = primaryPickup?.lng;
    const deliveryLat = item.deliveryLat || item.deliveryAddress?.latitude;
    const deliveryLng = item.deliveryLng || item.deliveryAddress?.longitude;
    if (pickupLat && pickupLng && deliveryLat && deliveryLng) {
      return haversineKm(
        Number(pickupLat),
        Number(pickupLng),
        Number(deliveryLat),
        Number(deliveryLng)
      ).toFixed(1);
    }
    return null;
  }, [
    item.distanceKm,
    item.deliveryLat,
    item.deliveryLng,
    item.deliveryAddress,
    primaryPickup?.lat,
    primaryPickup?.lng,
  ]);

  const parcelLabel = getParcelTypeLabel(item.parcelType, item.category);
  const displayLabel =
    typeof parcelLabel === 'string' && parcelLabel.toUpperCase() === 'GENERAL'
      ? t('bulkBooking.categoryGeneral')
      : parcelLabel === t('bulkBooking.other')
        ? item.category || t('bulkBooking.categoryGeneral')
        : parcelLabel;

  return (
    <>
      <TableRow
        sx={{
          // Entry animation (staggered by index)
          '@keyframes fadeUp': {
            '0%': { opacity: 0, transform: 'translateY(8px)' },
            '100%': { opacity: 1, transform: 'translateY(0)' },
          },
          animation: `fadeUp 420ms ease-out ${index * 50}ms both`,
          transition: 'all 200ms ease',
          '& td': { py: 0.75, px: 1.5, borderBottom: '1px solid #f1f5f9' },
        }}
      >
        {/* Row number */}
        <TableCell>
          <Typography
            sx={{
              fontWeight: 700,
              color: BUSINESS_COLORS.primary,
              fontSize: '0.875rem',
            }}
          >
            {index + 1}
          </Typography>
        </TableCell>

        {/* Pickup */}
        <TableCell>
          <Box sx={{ p: 0.5 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={item.usePrimaryPickup !== false}
                  onChange={handleUsePrimaryToggle}
                  size="small"
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: '#f59e0b',
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      bgcolor: '#f59e0b',
                    },
                  }}
                />
              }
              label={
                <Typography sx={{ fontSize: '0.75rem', color: '#64748b' }}>
                  {t('bulkBooking.usePrimaryAddress')}
                </Typography>
              }
              sx={{ m: 0 }}
            />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
              <LocationIcon sx={{ fontSize: 14, color: '#10b981' }} />
              <Typography
                sx={{
                  fontSize: '0.8rem',
                  color: BUSINESS_COLORS.primary,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: 180,
                }}
              >
                {item.usePrimaryPickup !== false
                  ? formatAddress(primaryPickup?.address)
                  : formatAddress(item.pickupAddress)}
              </Typography>
            </Box>
          </Box>
        </TableCell>

        {/* Delivery */}
        <TableCell>
          <Box
            onClick={() => handleToggleAccordion('address')}
            sx={{
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              p: 0.75,
              borderRadius: 2,
              border: hasDelivery ? '1px solid #e5e7eb' : `1px dashed #f59e0b`,
              bgcolor: hasDelivery ? '#fafafa' : alpha('#f59e0b', 0.05),
              transition: 'all 200ms',
              '&:hover': {
                bgcolor: hasDelivery ? '#f5f5f5' : alpha('#f59e0b', 0.1),
                transform: 'translateX(2px)',
              },
            }}
          >
            <LocationIcon
              sx={{
                fontSize: 16,
                color: hasDelivery ? '#f59e0b' : '#94a3b8',
              }}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: '0.8rem',
                  color: hasDelivery ? '#111827' : '#94a3b8',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  fontWeight: hasDelivery ? 600 : 400,
                }}
              >
                {hasDelivery
                  ? formatAddress(item.deliveryAddress)
                  : t('bulkBooking.clickToAddAddress')}
              </Typography>
              {item.recipientName && (
                <Typography sx={{ fontSize: '0.7rem', color: '#64748b' }}>
                  {item.recipientName} {item.recipientPhone && `• ${item.recipientPhone}`}
                </Typography>
              )}
              {displayDistanceKm && (
                <Typography
                  sx={{ fontSize: '0.65rem', color: '#10b981', fontWeight: 600, mt: 0.25 }}
                >
                  ~{displayDistanceKm} km
                </Typography>
              )}
            </Box>
            <EditIcon sx={{ fontSize: 14, color: '#94a3b8' }} />
          </Box>
        </TableCell>

        {/* Weight */}
        <TableCell>
          <TextField
            type="number"
            size="small"
            value={localWeight}
            onChange={handleWeightChange}
            onBlur={handleWeightBlur}
            inputProps={{ min: 0.1, max: 100, step: 0.1 }}
            sx={{
              width: 100,
              '& .MuiOutlinedInput-root': {
                fontSize: '0.85rem',
                '& fieldset': { borderColor: '#e2e8f0' },
              },
            }}
            InputProps={{
              endAdornment: (
                <Typography sx={{ fontSize: '0.7rem', color: '#94a3b8', ml: 0.5 }}>kg</Typography>
              ),
            }}
          />
        </TableCell>

        {/* Volume */}
        {/* <TableCell>
          <TextField
            type="number"
            size="small"
            value={localVolume}
            onChange={handleVolumeChange}
            onBlur={handleVolumeBlur}
            inputProps={{ min: 0, max: 10, step: 0.01 }}
            sx={{
              width: 80,
              '& .MuiOutlinedInput-root': {
                fontSize: '0.85rem',
                '& fieldset': { borderColor: '#e2e8f0' },
              },
            }}
            InputProps={{
              endAdornment: (
                <Typography sx={{ fontSize: '0.65rem', color: '#94a3b8', ml: 0.5 }}>
                  m³
                </Typography>
              ),
            }}
          />
        </TableCell> */}
        {/* Volume */}
        <TableCell>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              cursor: 'pointer',
            }}
            onClick={() => handleToggleAccordion('volume')}
          >
            <Tooltip
              title={
                volumeDims && (volumeDims.length || volumeDims.breadth || volumeDims.height)
                  ? `${volumeDims.length || 0}×${volumeDims.breadth || 0}×${volumeDims.height || 0} cm`
                  : t('bulkBooking.clickToSetDimensions')
              }
              arrow
            >
              <Typography
                sx={{
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  color: displayVolume ? '#111827' : '#94a3b8',
                }}
              >
                {displayVolume !== null ? `${displayVolume}` : '0.0000'}
              </Typography>
            </Tooltip>
            <Typography sx={{ fontSize: '0.7rem', color: '#94a3b8' }}>m³</Typography>
            <IconButton size="small" sx={{ ml: 'auto', p: 0.25 }}>
              <ExpandMoreIcon
                sx={{
                  fontSize: 14,
                  color: '#10b981',
                  transform: showVolumeEditor ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 200ms',
                }}
              />
            </IconButton>
          </Box>
        </TableCell>

        {/* Category */}
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip
                label={displayLabel}
                size="small"
                onClick={() => handleToggleAccordion('category')}
                sx={{
                  fontWeight: 600,
                  fontSize: '0.7rem',
                  bgcolor: alpha('#f59e0b', 0.12),
                  color: '#111827',
                  border: '1px solid',
                  borderColor: alpha('#f59e0b', 0.2),
                  cursor: 'pointer',
                  '&:hover': { bgcolor: alpha('#f59e0b', 0.18) },
                }}
              />

              {showBulkBadge && bulkOrder && bulkOrder.bulkOrderReference && (
                <BulkBadge
                  bulkOrderId={bulkOrder.bulkOrderId}
                  bulkOrderReference={bulkOrder.bulkOrderReference}
                  itemCount={bulkOrder.itemCount}
                />
              )}
            </Box>
            <IconButton size="small" onClick={() => handleToggleAccordion('category')}>
              <ExpandMoreIcon
                sx={{
                  fontSize: 16,
                  color: '#f59e0b',
                  transform: showCategoryEditor ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 200ms',
                }}
              />
            </IconButton>
          </Box>
        </TableCell>

        {/* Price */}
        <TableCell align="right">
          {item.itemPrice ? (
            <Box
              sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}
            >
              {/* Per-item savings badge — shown when bulk saves vs individual booking */}
              {(() => {
                const savings = Number(bulkOrder?.routeSavingsPercent || 0);
                if (savings > 0) {
                  const bulkPrice = Number(item.itemPrice);
                  // Reverse-calculate what the individual price would have been
                  const individualPrice = Math.round(bulkPrice / (1 - savings / 100));
                  const savedAmount = individualPrice - Math.round(bulkPrice);
                  if (savedAmount > 0) {
                    return (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography
                          sx={{
                            fontSize: '0.65rem',
                            color: '#9ca3af',
                            textDecoration: 'line-through',
                          }}
                        >
                          ₹{individualPrice}
                        </Typography>
                        <Chip
                          label={`-₹${savedAmount}`}
                          size="small"
                          sx={{
                            height: 16,
                            fontSize: '0.58rem',
                            fontWeight: 700,
                            bgcolor: alpha('#10b981', 0.12),
                            color: '#10b981',
                            border: '1px solid',
                            borderColor: alpha('#10b981', 0.25),
                            '& .MuiChip-label': { px: 0.6 },
                          }}
                        />
                      </Box>
                    );
                  }
                }
                return null;
              })()}
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  bgcolor: alpha('#10b981', 0.12),
                  px: 1.25,
                  py: 0.5,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: alpha('#10b981', 0.2),
                }}
              >
                <Typography sx={{ fontSize: '0.7rem', color: '#10b981', fontWeight: 700 }}>
                  ₹
                </Typography>
                <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, color: '#111827' }}>
                  {Math.round(item.itemPrice)}
                </Typography>
              </Box>
              {item.clusterId != null &&
                (() => {
                  const clusterColor = CLUSTER_COLORS[(item.clusterId - 1) % CLUSTER_COLORS.length];
                  return (
                    <Chip
                      label={
                        item.isMultiStop
                          ? `Route ${item.clusterId}`
                          : `Route ${item.clusterId} · solo`
                      }
                      size="small"
                      sx={{
                        height: 18,
                        fontSize: '0.6rem',
                        fontWeight: 700,
                        bgcolor: alpha(clusterColor, 0.12),
                        color: clusterColor,
                        border: '1px solid',
                        borderColor: alpha(clusterColor, 0.25),
                        '& .MuiChip-label': { px: 0.75 },
                      }}
                    />
                  );
                })()}
            </Box>
          ) : (
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
              <Typography
                sx={{
                  fontSize: '0.75rem',
                  color: '#94a3b8',
                  fontStyle: 'italic',
                }}
              >
                {t('bulkBooking.calculate')}
              </Typography>
            </Box>
          )}
        </TableCell>

        {/* Actions */}
        <TableCell align="center">
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.75 }}>
            <Tooltip title={t('bulkBooking.duplicateRow')} arrow>
              <IconButton
                size="small"
                onClick={() => onDuplicate(item.itemId)}
                sx={{
                  bgcolor: alpha('#f59e0b', 0.1),
                  borderRadius: 1.5,
                  transition: 'all 200ms',
                  '&:hover': {
                    bgcolor: alpha('#f59e0b', 0.2),
                    transform: 'scale(1.05)',
                  },
                }}
              >
                <CopyIcon sx={{ fontSize: 16, color: '#f59e0b' }} />
              </IconButton>
            </Tooltip>
            <Tooltip title={t('bulkBooking.deleteRow')} arrow>
              <IconButton
                size="small"
                onClick={() => onDelete(item.itemId)}
                sx={{
                  bgcolor: alpha('#ef4444', 0.1),
                  borderRadius: 1.5,
                  transition: 'all 200ms',
                  '&:hover': {
                    bgcolor: alpha('#ef4444', 0.2),
                    transform: 'scale(1.05)',
                  },
                }}
              >
                <DeleteIcon sx={{ fontSize: 16, color: '#ef4444' }} />
              </IconButton>
            </Tooltip>
          </Box>
        </TableCell>
      </TableRow>

      {/* Expandable delivery address accordion */}
      {isExpanded && (
        <TableRow>
          <TableCell colSpan={8} sx={{ py: 0, px: 0, border: 'none' }}>
            <Box
              sx={{
                p: 2,
                bgcolor: '#fefaf3',
                borderLeft: '3px solid #f59e0b',
                animation: 'slideDown 280ms ease-out',
                '@keyframes slideDown': {
                  '0%': { opacity: 0, transform: 'translateY(-8px)' },
                  '100%': { opacity: 1, transform: 'translateY(0)' },
                },
              }}
            >
              <DeliveryAddressAccordion
                item={item}
                onSave={handleDeliveryUpdate}
                onCancel={() => setIsExpanded(false)}
              />
            </Box>
          </TableCell>
        </TableRow>
      )}

      {/* Dimensions editor */}
      {showVolumeEditor && (
        <TableRow>
          <TableCell colSpan={4} sx={{ py: 0, px: 0, border: 'none' }}>
            <Box
              sx={{
                p: 2,
                bgcolor: '#f0fdf4',
                borderLeft: '3px solid #10b981',
                animation: 'slideDown 280ms ease-out',
              }}
            >
              <DimensionsInput
                volume={volumeDims}
                onChange={handleVolumeDimsChange}
                compact={false}
              />
              <Stack direction="row" spacing={1} sx={{ mt: 1.5, justifyContent: 'flex-end' }}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setShowVolumeEditor(false)}
                  sx={{
                    textTransform: 'none',
                    fontSize: '0.75rem',
                    borderColor: '#d1d5db',
                    color: '#6b7280',
                  }}
                >
                  {t('bulkBooking.cancel')}
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => {
                    handleVolumeDimsBlur();
                    setShowVolumeEditor(false);
                  }}
                  sx={{
                    textTransform: 'none',
                    fontSize: '0.75rem',
                    bgcolor: '#10b981',
                    '&:hover': { bgcolor: '#059669' },
                  }}
                >
                  {t('bulkBooking.apply')}
                </Button>
              </Stack>
            </Box>
          </TableCell>
        </TableRow>
      )}

      {/* Category editor */}
      {showCategoryEditor && (
        <TableRow>
          <TableCell colSpan={8} sx={{ py: 0, px: 0, border: 'none' }}>
            <Box
              sx={{
                p: 2,
                bgcolor: '#fffbeb',
                borderLeft: '3px solid #f59e0b',
                animation: 'slideDown 280ms ease-out',
              }}
            >
              <ParcelTypeSelector
                selectedTypes={selectedTypes}
                onToggle={handleTypeToggle}
                compact={false}
              />
              <Stack direction="row" spacing={1} sx={{ mt: 1.5, justifyContent: 'flex-end' }}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setShowCategoryEditor(false)}
                  sx={{
                    textTransform: 'none',
                    fontSize: '0.75rem',
                    borderColor: '#d1d5db',
                    color: '#6b7280',
                  }}
                >
                  {t('bulkBooking.cancel')}
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  onClick={applyTypes}
                  sx={{
                    textTransform: 'none',
                    fontSize: '0.75rem',
                    bgcolor: '#f59e0b',
                    '&:hover': { bgcolor: '#d97706' },
                  }}
                >
                  {t('bulkBooking.apply')}
                </Button>
              </Stack>
            </Box>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

export default memo(BulkBookingRow);
